import { useEffect, useMemo, useRef, useState } from 'react';
import { useMenu } from '@/features/menu/useMenu';
import type {
  MenuCategoryDto,
  MenuItemDto,
  MenuSectionDto,
} from '@tg-hookah/shared/dto';
import { Spinner } from '@/components/Spinner';

export function MenuPage() {
  const { data, isLoading } = useMenu();
  const categories = useMemo(() => data ?? [], [data]);
  const [activeId, setActiveId] = useState<string>('');
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const scrollingProgrammatically = useRef(false);

  const categoryIds = useMemo(() => categories.map((c) => c.id), [categories]);

  // Pick first category once data arrives.
  useEffect(() => {
    if (!activeId && categoryIds[0]) setActiveId(categoryIds[0]);
  }, [categoryIds, activeId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (scrollingProgrammatically.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target) {
          const id = (visible.target as HTMLElement).dataset.catId;
          if (id) setActiveId(id);
        }
      },
      { rootMargin: '-30% 0px -60% 0px', threshold: [0, 0.1, 0.5, 1] },
    );
    for (const id of categoryIds) {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [categoryIds]);

  useEffect(() => {
    const btn = tabRefs.current[activeId];
    btn?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeId]);

  const scrollTo = (id: string) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    scrollingProgrammatically.current = true;
    setActiveId(id);
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.setTimeout(() => {
      scrollingProgrammatically.current = false;
    }, 600);
  };

  if (isLoading)
    return (
      <div className="py-10 text-center text-hint">
        <Spinner />
      </div>
    );

  if (!categories.length)
    return <p className="text-hint text-sm py-6">Меню пока пустое</p>;

  return (
    <div className="-mx-4 flex flex-col">
      <nav
        aria-label="menu categories"
        className="sticky top-[calc(env(safe-area-inset-top)+56px)] z-20 bg-bg/90 backdrop-blur border-b border-white/5"
      >
        <ul className="flex gap-2 overflow-x-auto px-4 py-3 no-scrollbar">
          {categories.map((cat) => {
            const active = cat.id === activeId;
            return (
              <li key={cat.id}>
                <button
                  ref={(el) => {
                    tabRefs.current[cat.id] = el;
                  }}
                  type="button"
                  onClick={() => scrollTo(cat.id)}
                  aria-pressed={active}
                  className={
                    'whitespace-nowrap rounded-full px-4 py-2 text-xs font-display uppercase tracking-[0.08em] transition-colors border ' +
                    (active
                      ? 'bg-white text-black border-white'
                      : 'bg-white/[0.03] text-white/70 border-white/10 hover:text-white hover:border-white/25')
                  }
                >
                  {cat.title}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="px-4 pt-4 pb-8 flex flex-col gap-8">
        {categories.map((cat) => (
          <CategoryBlock
            key={cat.id}
            category={cat}
            registerRef={(el) => {
              sectionRefs.current[cat.id] = el;
            }}
          />
        ))}
      </div>
    </div>
  );
}

function CategoryBlock({
  category,
  registerRef,
}: {
  category: MenuCategoryDto;
  registerRef: (el: HTMLElement | null) => void;
}) {
  return (
    <section
      ref={registerRef}
      data-cat-id={category.id}
      className="scroll-mt-32"
      aria-label={category.title}
    >
      <header className="mb-3">
        <h2 className="headline text-[26px] uppercase tracking-[0.01em]">
          {category.title}
        </h2>
        <div className="hairline mt-2 w-12" />
      </header>
      <div className="flex flex-col gap-5">
        {category.sections.map((sec) => (
          <SectionBlock key={sec.id} section={sec} />
        ))}
      </div>
    </section>
  );
}

function SectionBlock({ section }: { section: MenuSectionDto }) {
  return (
    <div
      className="rounded-2xl p-4"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid var(--line-1)',
      }}
    >
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className="text-[11px] uppercase tracking-[0.22em] text-white/55 font-mono">
          {section.title}
        </h3>
        {section.note ? (
          <span className="text-[11px] font-mono text-white/40 shrink-0">{section.note}</span>
        ) : null}
      </div>

      {section.paragraphs?.length ? (
        <div className="flex flex-col gap-2 text-sm text-text/90">
          {section.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      ) : null}

      {section.plainItems?.length ? (
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-text/90">
          {section.plainItems.map((item) => (
            <li key={item} className="truncate">
              · {item}
            </li>
          ))}
        </ul>
      ) : null}

      {section.items?.length ? (
        <ul className="flex flex-col divide-y divide-white/5">
          {section.items.map((item) => (
            <ItemRow key={item.id} item={item} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ItemRow({ item }: { item: MenuItemDto }) {
  const [open, setOpen] = useState(false);
  const hasPhoto = Boolean(item.photoUrl);
  return (
    <li className="py-2.5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            {hasPhoto ? (
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-track="click:menu-item-photo"
                className="font-medium text-text text-left underline decoration-dotted underline-offset-4"
              >
                {item.name}
              </button>
            ) : (
              <span className="font-medium text-text">{item.name}</span>
            )}
            {item.subtitle ? (
              <span className="text-xs text-hint">· {item.subtitle}</span>
            ) : null}
          </div>
          {item.description ? (
            <div className="text-xs text-hint mt-0.5 leading-snug">{item.description}</div>
          ) : null}
          {item.note ? (
            <div className="text-[11px] text-hint/80 mt-0.5">{item.note}</div>
          ) : null}
          {hasPhoto && open ? (
            <img
              src={item.photoUrl as string}
              alt={item.name}
              className="mt-2 rounded-lg w-full max-w-[260px] object-cover"
            />
          ) : null}
        </div>
        <div className="shrink-0 font-semibold tabular-nums text-text/95">
          {item.price}
          <span className="text-hint font-normal ml-0.5">₽</span>
        </div>
      </div>
    </li>
  );
}
